/**
 * Scale drop form – picker/template handlers
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback } from 'react';
import {
  filterSenderNamesMax11,
  MAX_SELECTED_TEXTS,
  parseSenderNamesInput,
  parseSmsTextsInput,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';

export function useScaleDropFormHandlers(params: {
  sendersList: string[];
  filteredSenders: string[];
  filteredTexts: string[];
  newSenderValue: string;
  newTextValue: string;
  selectedSenderNames: string[];
  selectedSmsTexts: string[];
  effectiveRouteIdForTemplates: number;
  setSelectedSenderNames: (v: string[] | ((p: string[]) => string[])) => void;
  setSelectedSmsTexts: (v: string[] | ((p: string[]) => string[])) => void;
  setLocalSenders: (v: string[] | ((p: string[]) => string[])) => void;
  setLocalTexts: (v: string[] | ((p: string[]) => string[])) => void;
  setReplacedByAiTexts?: (v: string[] | ((p: string[]) => string[])) => void;
  setPerDropSmsTexts?: (v: string[][] | null | ((p: string[][] | null) => string[][] | null)) => void;
  setReplacedByAiSenders?: (v: string[] | ((p: string[]) => string[])) => void;
  setPerDropSenderNames?: (v: string[][] | null | ((p: string[][] | null) => string[][] | null)) => void;
  setNewSenderValue: (v: string) => void;
  setNewTextValue: (v: string) => void;
  setActiveTemplateId: (v: string | null) => void;
  lastSyncedRef: React.MutableRefObject<string>;
  createTemplate: { mutateAsync: (p: { name: string; senderNames: string[] }) => Promise<unknown> };
  onAddSender?: (name: string) => Promise<void>;
  onAddText?: (texts: string[]) => Promise<void>;
  onDeleteSender?: (name: string) => Promise<void>;
  onDeleteText?: (text: string) => Promise<void>;
  onDeleteTextsBulk?: (texts: string[]) => Promise<void>;
}) {
  const {
    sendersList,
    filteredSenders,
    filteredTexts,
    newSenderValue,
    newTextValue,
    selectedSenderNames,
    selectedSmsTexts,
    effectiveRouteIdForTemplates,
    setSelectedSenderNames,
    setSelectedSmsTexts,
    setLocalSenders,
    setLocalTexts,
    setReplacedByAiTexts,
    setPerDropSmsTexts,
    setReplacedByAiSenders,
    setPerDropSenderNames,
    setNewSenderValue,
    setNewTextValue,
    setActiveTemplateId,
    lastSyncedRef,
    createTemplate,
    onAddSender,
    onAddText,
    onDeleteSender,
    onDeleteText,
    onDeleteTextsBulk,
  } = params;

  const toggleSender = useCallback((name: string) => {
    setSelectedSenderNames((prev) => (prev.includes(name) ? prev.filter((v) => v !== name) : [...prev, name]));
  }, [setSelectedSenderNames]);

  const handleApplyTemplate = useCallback(async (senders: string[]) => {
    setSelectedSenderNames(senders);
    const missing = senders.filter((s) => !sendersList.includes(s));
    if (missing.length > 0) {
      setLocalSenders((prev) => uniqueKeepOrderStrings([...prev, ...missing]));
      for (const name of missing) await onAddSender?.(name);
    }
  }, [sendersList, onAddSender, setSelectedSenderNames, setLocalSenders]);

  const handleSelectTemplate = useCallback(async (template: { id: string; senderNames: string[] } | null) => {
    if (!template) { setActiveTemplateId(null); return; }
    setActiveTemplateId(template.id);
    lastSyncedRef.current = JSON.stringify([...template.senderNames].sort());
    await handleApplyTemplate(template.senderNames);
  }, [handleApplyTemplate, setActiveTemplateId, lastSyncedRef]);

  const handleSaveAsTemplate = useCallback(async () => {
    if (selectedSenderNames.length === 0 || effectiveRouteIdForTemplates <= 0) return;
    const firstName = selectedSenderNames[0] ?? 'Template';
    const name = selectedSenderNames.length > 1 ? `${firstName} +${selectedSenderNames.length - 1}` : firstName;
    await createTemplate.mutateAsync({ name, senderNames: selectedSenderNames });
  }, [selectedSenderNames, effectiveRouteIdForTemplates, createTemplate]);

  const toggleText = useCallback((text: string) => {
    setSelectedSmsTexts((prev) =>
      prev.includes(text) ? prev.filter((v) => v !== text)
        : prev.length >= MAX_SELECTED_TEXTS ? prev : [...prev, text],
    );
  }, [setSelectedSmsTexts]);

  const selectAllSenders = useCallback(
    () => setSelectedSenderNames((prev) => (prev.length === filteredSenders.length ? [] : [...filteredSenders])),
    [filteredSenders, setSelectedSenderNames],
  );
  const selectAllTexts = useCallback(
    () => setSelectedSmsTexts((prev) => {
      const capped = filteredTexts.slice(0, MAX_SELECTED_TEXTS);
      return prev.length >= capped.length ? [] : capped;
    }),
    [filteredTexts, setSelectedSmsTexts],
  );

  const handleAddSender = useCallback(async () => {
    if (!onAddSender) return;
    const { senders } = parseSenderNamesInput(newSenderValue);
    const valid = filterSenderNamesMax11(senders);
    if (valid.length === 0) return;
    setSelectedSenderNames((prev) => {
      const n = valid.filter((x) => !prev.includes(x));
      return n.length ? uniqueKeepOrderStrings([...prev, ...n]) : prev;
    });
    setLocalSenders((prev) => {
      const n = valid.filter((x) => !prev.includes(x));
      return n.length ? uniqueKeepOrderStrings([...prev, ...n]) : prev;
    });
    setNewSenderValue('');
    for (const name of valid) await onAddSender(name);
  }, [newSenderValue, onAddSender, setSelectedSenderNames, setLocalSenders, setNewSenderValue]);

  const handleAddText = useCallback(async () => {
    if (!onAddText) return;
    const { texts } = parseSmsTextsInput(newTextValue);
    if (texts.length === 0) return;
    setSelectedSmsTexts((prev) => {
      const n = texts.filter((t) => !prev.includes(t));
      return n.length ? uniqueKeepOrderStrings([...prev, ...n]).slice(0, MAX_SELECTED_TEXTS) : prev;
    });
    setLocalTexts((prev) => {
      const n = texts.filter((t) => !prev.includes(t));
      return n.length ? uniqueKeepOrderStrings([...prev, ...n]) : prev;
    });
    setNewTextValue('');
    await onAddText(texts);
  }, [newTextValue, onAddText, setSelectedSmsTexts, setLocalTexts, setNewTextValue]);

  const clearSendersSelection = useCallback(() => setSelectedSenderNames([]), [setSelectedSenderNames]);
  const clearTextsSelection = useCallback(() => setSelectedSmsTexts([]), [setSelectedSmsTexts]);
  const handleDeleteSender = useCallback(
    async (name: string) => {
      await onDeleteSender?.(name);
      setLocalSenders((prev) => prev.filter((s) => s !== name));
    },
    [onDeleteSender, setLocalSenders],
  );

  const handleDeleteSenders = useCallback(
    async (names: string[]) => {
      if (names.length === 0) return;
      const deleteSet = new Set(names);
      await Promise.all(names.map((name) => onDeleteSender?.(name)));
      setLocalSenders((prev) => prev.filter((s) => !deleteSet.has(s)));
    },
    [onDeleteSender, setLocalSenders],
  );
  const handleDeleteText = useCallback(
    async (text: string) => {
      await onDeleteText?.(text);
      setLocalTexts((prev) => prev.filter((t) => t !== text));
    },
    [onDeleteText, setLocalTexts],
  );

  const handleDeleteTextsBulk = useCallback(
    async (texts: string[]) => {
      const deleteSet = new Set(texts);
      if (onDeleteTextsBulk) await onDeleteTextsBulk(texts);
      else if (onDeleteText) for (const t of texts) await onDeleteText(t);
      setLocalTexts((prev) => prev.filter((t) => !deleteSet.has(t)));
    },
    [onDeleteText, onDeleteTextsBulk, setLocalTexts],
  );

  const autoSelectTexts = useCallback((texts: string[]) => {
    setLocalTexts((prev) => uniqueKeepOrderStrings([...prev, ...texts]));
    setSelectedSmsTexts((prev) =>
      uniqueKeepOrderStrings([...prev, ...texts]).slice(0, MAX_SELECTED_TEXTS),
    );
  }, [setLocalTexts, setSelectedSmsTexts]);

  /** Adds generated texts, selects ONLY them, marks old selection as replaced (distinct color). */
  const saveGeneratedAndSelectOnly = useCallback((newTexts: string[]) => {
    setLocalTexts((prev) => uniqueKeepOrderStrings([...prev, ...newTexts]));
    setReplacedByAiTexts?.((prev) => uniqueKeepOrderStrings([...prev, ...selectedSmsTexts]));
    setSelectedSmsTexts(newTexts.slice(0, MAX_SELECTED_TEXTS));
    setPerDropSmsTexts?.(null);
  }, [setLocalTexts, setReplacedByAiTexts, setSelectedSmsTexts, setPerDropSmsTexts, selectedSmsTexts]);

  /** Adds generated senders, selects ONLY them, marks old selection as replaced. */
  const saveGeneratedSendersAndSelectOnly = useCallback((newSenders: string[]) => {
    setLocalSenders((prev) => uniqueKeepOrderStrings([...prev, ...newSenders]));
    setReplacedByAiSenders?.((prev) => uniqueKeepOrderStrings([...prev, ...selectedSenderNames]));
    setSelectedSenderNames(newSenders.slice(0, 50));
    setPerDropSenderNames?.(null);
  }, [setLocalSenders, setReplacedByAiSenders, setSelectedSenderNames, setPerDropSenderNames, selectedSenderNames]);

  return {
    toggleSender,
    toggleText,
    selectAllSenders,
    selectAllTexts,
    handleAddSender,
    handleAddText,
    handleSelectTemplate,
    handleSaveAsTemplate,
    handleApplyTemplate,
    clearSendersSelection,
    clearTextsSelection,
    handleDeleteSender,
    handleDeleteSenders,
    handleDeleteText,
    handleDeleteTextsBulk,
    autoSelectTexts,
    saveGeneratedAndSelectOnly,
    saveGeneratedSendersAndSelectOnly,
  };
}
